import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, useCan } from "@/components/common";
import { FotoImg } from "@/components/IAExplain";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ClipboardCheck, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TIPOS_PLANTILLA = ["PCC", "prerrequisito", "higiene", "limpieza"];
const TIPOS_CAMPO = ["numero", "texto", "booleano"];

export default function Calidad() {
  const { user, scopeTenant } = useAuth();
  const canEdit = useCan(user) || user.rol === "operario";
  const needsScope = useScopeGuard(user, scopeTenant);
  const [plantillas, setPlantillas] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [revisiones, setRevisiones] = useState([]);
  const [presets, setPresets] = useState([]);

  // dialogs
  const [revOpen, setRevOpen] = useState(false);
  const [rev, setRev] = useState({ zona: "", observacion: "", conforme: true });
  const [revFotos, setRevFotos] = useState([]);

  const [regOpen, setRegOpen] = useState(false);
  const [regPlantilla, setRegPlantilla] = useState(null);
  const [regValores, setRegValores] = useState({});
  const [regZona, setRegZona] = useState("");

  const [plaOpen, setPlaOpen] = useState(false);
  const [pla, setPla] = useState({ nombre: "", tipo: "prerrequisito", requisito_id: "", campos: [] });

  const load = useCallback(async () => {
    if (needsScope) return;
    const [p, r, rev] = await Promise.all([
      api.get("/calidad/plantillas"), api.get("/calidad/registros"), api.get("/calidad/revisiones"),
    ]);
    setPlantillas(p.data); setRegistros(r.data); setRevisiones(rev.data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/calidad/presets").then((r) => setPresets(r.data)).catch(() => {}); }, []);

  const agregarPreset = async (id) => {
    try { await api.post(`/calidad/presets/${id}`); toast.success("Plantilla añadida"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const enviarRevision = async () => {
    if (!rev.zona || !rev.observacion) return toast.error("Zona y observación son obligatorias");
    const fd = new FormData();
    fd.append("zona", rev.zona); fd.append("observacion", rev.observacion);
    fd.append("conforme", rev.conforme);
    revFotos.slice(0, 5).forEach((f) => fd.append("fotos", f));
    try {
      const { data } = await api.post("/calidad/revision-libre", fd);
      toast.success(rev.conforme ? "Revisión registrada" : "Revisión NO conforme · incidencia creada");
      setRevOpen(false); setRev({ zona: "", observacion: "", conforme: true }); setRevFotos([]);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const abrirRegistro = (p) => {
    setRegPlantilla(p); setRegValores({}); setRegZona(""); setRegOpen(true);
  };
  const enviarRegistro = async () => {
    try {
      const { data } = await api.post("/calidad/registros", {
        plantilla_id: regPlantilla.id, valores: regValores, zona: regZona,
      });
      if (data.conforme) toast.success("Registro conforme guardado");
      else toast.warning("Registro NO conforme · incidencia generada automáticamente");
      setRegOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const addCampo = () => setPla((p) => ({ ...p, campos: [...p.campos, { nombre: "", tipo: "numero", unidad: "", limite_min: "", limite_max: "", requerido: true }] }));
  const setCampo = (i, k, v) => setPla((p) => ({ ...p, campos: p.campos.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const delCampo = (i) => setPla((p) => ({ ...p, campos: p.campos.filter((_, idx) => idx !== i) }));

  const crearPlantilla = async () => {
    if (!pla.nombre || pla.campos.length === 0) return toast.error("Nombre y al menos un campo");
    const campos = pla.campos.map((c) => ({
      nombre: c.nombre, tipo: c.tipo, unidad: c.unidad || null,
      limite_min: c.limite_min === "" ? null : Number(c.limite_min),
      limite_max: c.limite_max === "" ? null : Number(c.limite_max),
      requerido: c.requerido, opciones: null,
    }));
    try {
      await api.post("/calidad/plantillas", { nombre: pla.nombre, tipo: pla.tipo, requisito_id: pla.requisito_id || null, campos });
      toast.success("Plantilla creada");
      setPlaOpen(false); setPla({ nombre: "", tipo: "prerrequisito", requisito_id: "", campos: [] }); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="calidad-page">
      <PageHeader overline="Nivel 2 · Registra" title="Supervisión de Calidad" help="calidad"
        action={canEdit && (
          <div className="flex gap-2">
            <Dialog open={revOpen} onOpenChange={setRevOpen}>
              <DialogTrigger asChild>
                <Button data-testid="nueva-revision-btn" className="bg-[#FF5625] hover:bg-[#E0451A] text-base h-11 px-5">
                  <Camera size={18} className="mr-2" /> Nueva revisión
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-md" data-testid="revision-dialog">
                <DialogHeader><DialogTitle className="text-[#0E2A47]">Revisión de planta</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Zona / proceso</Label>
                    <Input value={rev.zona} onChange={(e) => setRev({ ...rev, zona: e.target.value })} data-testid="rev-zona" className="mt-1.5" placeholder="Cámara 2, línea de envasado…" /></div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Observación</Label>
                    <Textarea value={rev.observacion} onChange={(e) => setRev({ ...rev, observacion: e.target.value })} data-testid="rev-observacion" className="mt-1.5" /></div>
                  <div className="flex items-center justify-between border border-[#E2E8F0] rounded-md p-3">
                    <span className="text-sm font-medium text-[#0E2A47]">{rev.conforme ? "Conforme" : "No conforme"}</span>
                    <Switch checked={rev.conforme} onCheckedChange={(v) => setRev({ ...rev, conforme: v })} data-testid="rev-conforme" />
                  </div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Fotos (máx. 5)</Label>
                    <Input type="file" accept="image/*" multiple capture="environment" onChange={(e) => setRevFotos(Array.from(e.target.files))} data-testid="rev-fotos" className="mt-1.5" />
                    {revFotos.length > 0 && <div className="text-xs text-[#0E2A47]/50 mt-1">{revFotos.length} foto(s) seleccionada(s)</div>}
                  </div>
                </div>
                <DialogFooter><Button onClick={enviarRevision} data-testid="rev-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Registrar</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={plaOpen} onOpenChange={setPlaOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="nueva-plantilla-btn" className="h-11 border-[#0E2A47] text-[#0E2A47]"><Plus size={16} className="mr-1" /> Plantilla</Button>
              </DialogTrigger>
              <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="plantilla-dialog">
                <DialogHeader><DialogTitle className="text-[#0E2A47]">Nueva plantilla de registro</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Nombre</Label>
                      <Input value={pla.nombre} onChange={(e) => setPla({ ...pla, nombre: e.target.value })} data-testid="pla-nombre" className="mt-1.5" /></div>
                    <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Tipo</Label>
                      <Select value={pla.tipo} onValueChange={(v) => setPla({ ...pla, tipo: v })}>
                        <SelectTrigger data-testid="pla-tipo" className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">{TIPOS_PLANTILLA.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select></div>
                  </div>
                  <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Requisito IFS (opcional)</Label>
                    <Input value={pla.requisito_id} onChange={(e) => setPla({ ...pla, requisito_id: e.target.value })} data-testid="pla-requisito" className="mt-1.5" placeholder="4.9.1" /></div>
                  <div>
                    <div className="flex items-center justify-between"><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Campos</Label>
                      <Button size="sm" variant="ghost" onClick={addCampo} data-testid="pla-add-campo"><Plus size={14} /> Añadir</Button></div>
                    <div className="space-y-2 mt-2">
                      {pla.campos.map((c, i) => (
                        <div key={i} className="border border-[#E2E8F0] rounded-md p-2 space-y-2" data-testid={`pla-campo-${i}`}>
                          <div className="flex flex-wrap gap-2">
                            <Input placeholder="Nombre" value={c.nombre} onChange={(e) => setCampo(i, "nombre", e.target.value)} className="flex-1 min-w-[140px]" data-testid={`pla-campo-nombre-${i}`} />
                            <Select value={c.tipo} onValueChange={(v) => setCampo(i, "tipo", v)}>
                              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-white">{TIPOS_CAMPO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                            </Select>
                            <Button size="icon" variant="ghost" onClick={() => delCampo(i)}><Trash2 size={14} className="text-[#EF4444]" /></Button>
                          </div>
                          {c.tipo === "numero" && (
                            <div className="flex flex-wrap gap-2">
                              <Input placeholder="Unidad" value={c.unidad} onChange={(e) => setCampo(i, "unidad", e.target.value)} className="w-24" />
                              <Input placeholder="Mín" type="number" value={c.limite_min} onChange={(e) => setCampo(i, "limite_min", e.target.value)} className="w-24" />
                              <Input placeholder="Máx" type="number" value={c.limite_max} onChange={(e) => setCampo(i, "limite_max", e.target.value)} className="w-24" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter><Button onClick={crearPlantilla} data-testid="pla-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Crear plantilla</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )} />

      <Tabs defaultValue="revisiones">
        <TabsList className="bg-white border border-[#E2E8F0]">
          <TabsTrigger value="revisiones" data-testid="tab-revisiones">Revisiones ({revisiones.length})</TabsTrigger>
          <TabsTrigger value="registros" data-testid="tab-registros">Registros ({registros.length})</TabsTrigger>
          <TabsTrigger value="plantillas" data-testid="tab-plantillas">Plantillas ({plantillas.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="revisiones" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {revisiones.length === 0 && <div className="text-[#0E2A47]/50 text-sm">Sin revisiones aún.</div>}
            {revisiones.map((r) => (
              <Card key={r.id} className="p-4 border-[#E2E8F0]" data-testid={`revision-${r.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#0E2A47]">{r.zona}</span>
                  <Badge className={r.conforme ? "bg-[#10B981] hover:bg-[#10B981]" : "bg-[#FF5625] hover:bg-[#FF5625]"}>{r.conforme ? "Conforme" : "No conforme"}</Badge>
                </div>
                <p className="text-sm text-[#0E2A47]/70">{r.observacion}</p>
                {r.fotos?.length > 0 && (
                  <div className="flex gap-2 mt-3">{r.fotos.map((f) => <FotoImg key={f} id={f} className="w-16 h-16 object-cover rounded" />)}</div>
                )}
                <div className="text-xs text-[#0E2A47]/40 mt-2">{r.firmante_nombre} · {new Date(r.firma_fecha).toLocaleString("es-ES")}</div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="registros" className="mt-4">
          <Card className="border-[#E2E8F0] overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-[#F7F8FA]">
                <TableHead>Plantilla</TableHead><TableHead>Valores</TableHead><TableHead>Zona</TableHead><TableHead>Conforme</TableHead><TableHead>Firmado</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {registros.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-[#0E2A47]/50 py-8">Sin registros.</TableCell></TableRow>}
                {registros.map((r) => (
                  <TableRow key={r.id} data-testid={`registro-${r.id}`} className="hover:bg-[#F7F8FA]">
                    <TableCell className="font-medium text-[#0E2A47]">{r.plantilla_nombre}</TableCell>
                    <TableCell className="text-xs text-[#0E2A47]/70">{Object.entries(r.valores || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</TableCell>
                    <TableCell>{r.zona || "—"}</TableCell>
                    <TableCell><Badge className={r.conforme ? "bg-[#10B981] hover:bg-[#10B981]" : "bg-[#FF5625] hover:bg-[#FF5625]"}>{r.conforme ? "Sí" : "No"}</Badge></TableCell>
                    <TableCell className="text-xs text-[#0E2A47]/50">{r.firmante_nombre}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="plantillas" className="mt-4">
          {canEdit && presets.length > 0 && (
            <Card className="p-5 border-[#E2E8F0] mb-4" data-testid="presets-card">
              <div className="font-black tracking-tight text-[#0E2A47] mb-1">Plantillas recomendadas</div>
              <p className="text-sm text-[#0E2A47]/60 mb-3">Registros prehechos y mapeados a IFS Food v8. Añádelos con un clic y empieza a registrar.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {presets.map((pr) => {
                  const added = plantillas.some((pl) => pl.preset_id === pr.id);
                  return (
                    <div key={pr.id} data-testid={`preset-${pr.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-[#E2E8F0] p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#0E2A47] truncate">{pr.nombre}</div>
                        <div className="text-xs text-[#0E2A47]/50">{pr.campos.length} campos · req {pr.requisito_id}</div>
                      </div>
                      <Button size="sm" disabled={added} onClick={() => agregarPreset(pr.id)} data-testid={`preset-add-${pr.id}`}
                        className={added ? "border border-[#10B981] text-[#10B981] bg-white hover:bg-white" : "bg-[#0E2A47] hover:bg-[#163C65]"}>
                        {added ? "Añadida" : "Añadir"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plantillas.map((p) => (
              <Card key={p.id} className="p-5 border-[#E2E8F0]" data-testid={`plantilla-${p.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#0E2A47]">{p.nombre}</span>
                  <Badge variant="outline" className="border-[#E2E8F0] uppercase text-[10px]">{p.tipo}</Badge>
                </div>
                <div className="text-xs text-[#0E2A47]/50 mb-3">{p.campos.length} campos {p.requisito_id && `· req ${p.requisito_id}`}</div>
                {canEdit && <Button size="sm" onClick={() => abrirRegistro(p)} data-testid={`registrar-btn-${p.id}`} className="bg-[#FF5625] hover:bg-[#E0451A] w-full"><ClipboardCheck size={14} className="mr-1" /> Registrar</Button>}
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Registro dialog */}
      <Dialog open={regOpen} onOpenChange={setRegOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="registro-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">{regPlantilla?.nombre}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {regPlantilla?.campos.map((c) => (
              <div key={c.nombre}>
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">
                  {c.nombre} {c.unidad && `(${c.unidad})`}
                  {c.tipo === "numero" && (c.limite_min != null || c.limite_max != null) && (
                    <span className="text-[#FF5625] ml-1 normal-case">límite {c.limite_min ?? "-"}…{c.limite_max ?? "-"}</span>
                  )}
                </Label>
                {c.tipo === "booleano" ? (
                  <div className="mt-1.5"><Switch checked={!!regValores[c.nombre]} onCheckedChange={(v) => setRegValores({ ...regValores, [c.nombre]: v })} data-testid={`reg-campo-${c.nombre}`} /></div>
                ) : (
                  <Input type={c.tipo === "numero" ? "number" : "text"} value={regValores[c.nombre] ?? ""}
                    onChange={(e) => setRegValores({ ...regValores, [c.nombre]: c.tipo === "numero" ? e.target.value : e.target.value })}
                    data-testid={`reg-campo-${c.nombre}`} className="mt-1.5" />
                )}
              </div>
            ))}
            <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Zona</Label>
              <Input value={regZona} onChange={(e) => setRegZona(e.target.value)} data-testid="reg-zona" className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={enviarRegistro} data-testid="reg-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Firmar y guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
